// Quelle: /opt/lilasprojekte/frontend/src/App.tsx (Stand 2026-09-29)
// Geändert (SPEC 03 §7.1): Wegtabelle von Handschrift; die Schichtung Wurzel → Geschuetzt →
// Shell → Bildschirm bleibt; die Kartenwege fallen weg. Anmeldung mit ?next= statt state.von.
// ============================================================
// Wegweisung und Gerüst.
//
// Vier Ebenen, jede mit genau einer Aufgabe:
//   Wurzel       Meldungen und der EINE Aufruf von GET /api/me
//   Geschuetzt   Sitzung nötig; ohne → /anmelden?next=<hier>
//   Shell        Kopfleiste, Statusband, Reiterleiste, Chat, Live-Verbindung
//   Bildschirm   Auffangkorb (key = Pfad) + Suspense um den nachgeladenen Bildschirm
//
// Der Auffangkorb liegt ÜBER dem Outlet, aber UNTER der Kopfleiste: stürzt ein Bildschirm ab,
// soll man wegnavigieren können, statt neu zu laden. Sein `key` ist der Pfad — ohne ihn bliebe
// ein einmal abgestürzter Bildschirm stehen, wenn man längst woanders hingeklickt hat.
//
// Die Anmeldung wird EIFRIG geladen (sie ist für jeden Abgemeldeten der Weg hinein), alles
// andere über lazy(). Die Bildschirme der Auswertung, Belege, Themen, Exporte und Antworten
// werden unter ihren Namen aus SPEC 03 §2 geholt — benannter Export ODER Vorgabe-Export.
// ============================================================
import { Suspense, lazy, useEffect, type ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate, useParams, useRouteError, useSearchParams } from "react-router-dom";
import { aufloesen } from "./api/client";
import { Btn, Toaster } from "./components/ui";
import { Denkloader, LOADER_MS } from "./components/ladewerk";
import { ScreenBoundary } from "./components/app/ScreenBoundary";
import { Shell } from "./components/app/Shell";
import { Anmelden } from "./screens/Anmelden";
import { NichtGefunden } from "./screens/NichtGefunden";
import { Unbekannt } from "./screens/Unbekannt";
import { useIch } from "./hooks/useIch";
import { useVerzoegert } from "./hooks/useVerzoegert";
import { chunkRiegelLoesen, retryOnStaleShell } from "./lib/chunk";
import { bildschirmSchluessel } from "./lib/bildschirmSchluessel";
import { fehlerMelden, routenSetzen } from "./lib/fehlerbericht";
import { TX } from "./lib/texte";
import { ApiError } from "./lib/fetchJson";
import { istAdmin, verfahrenAusCode } from "./lib/rechte";
import { useAuthStore } from "./store/useAuthStore";
import { useStore } from "./store/useStore";

/**
 * Ein Bildschirm, nachgeladen. Er wird unter seinem Namen gesucht (benannter Export wie in
 * SPEC 03 §2), sonst als Vorgabe-Export — so bleibt die Wegtabelle unabhängig davon, wie die
 * Datei ihn ausführt. Ein veraltetes Bündel nach einem Deploy lädt einmal neu (lib/chunk).
 */
function bildschirm<M, K extends keyof M & string>(laden: () => Promise<M>, name: K) {
  // Der Typ des Bildschirms kommt aus dem Modul selbst: verlangt eine Komponente Props, die die
  // Wegtabelle nicht liefert, scheitert schon `tsc`. Mit `as ComponentType` (ohne Props) hieß
  // `<SeitenAnsicht />` ohne `dok`/`n` für den Prüfer „richtig“ — und stürzte im Browser ab
  // (`nf(undefined)`, Fehlerbericht 01.10.; seit E26 ersetzt die Dokumentansicht die Seitenansicht).
  type Komponente = M[K] extends ComponentType<infer P> ? ComponentType<P> : never;
  return lazy(async () => {
    const m = (await retryOnStaleShell(laden())) as Record<string, unknown>;
    const k = (m[name] ?? m.default) as Komponente | undefined;
    if (!k) throw new Error(`Bildschirm ${name} fehlt`);
    return { default: k };
  });
}

const VerfahrenBlatt = bildschirm(() => import("./screens/verfahren/VerfahrenBlatt"), "VerfahrenBlatt");
const Auswertung = bildschirm(() => import("./screens/auswertung/Auswertung"), "Auswertung");
const PruefenRahmen = bildschirm(() => import("./screens/pruefen/PruefenRahmen"), "PruefenRahmen");
const Warteschlange = bildschirm(() => import("./screens/pruefen/Warteschlange"), "Warteschlange");
const Boegen = bildschirm(() => import("./screens/pruefen/Boegen"), "Boegen");
const Gold = bildschirm(() => import("./screens/pruefen/Gold"), "Gold");
const Fragenliste = bildschirm(() => import("./screens/pruefen/Fragenliste"), "Fragenliste");
const Bogenvorlage = bildschirm(() => import("./screens/pruefen/Bogenvorlage"), "Bogenvorlage");
const OrtePruefen = bildschirm(() => import("./screens/pruefen/OrtePruefen"), "OrtePruefen");
const Zeichnungen = bildschirm(() => import("./screens/pruefen/Zeichnungen"), "Zeichnungen");
const Orte = bildschirm(() => import("./screens/orte/Orte"), "Orte");
// E32: die Karte (MapLibre) — eigener, nachgeladener Teil; nur dieser Bildschirm zahlt für MapLibre.
const Karte = bildschirm(() => import("./screens/karte/Karte"), "Karte");
const Eingang = bildschirm(() => import("./screens/eingang/Eingang"), "Eingang");
const Lauf = bildschirm(() => import("./screens/lauf/Lauf"), "Lauf");
const Themen = bildschirm(() => import("./screens/themen/Themen"), "Themen");
const Kodieren = bildschirm(() => import("./screens/themen/Kodieren"), "Kodieren");
const ThemaSeite = bildschirm(() => import("./screens/themen/ThemaSeite"), "ThemaSeite");
const Exporte = bildschirm(() => import("./screens/exporte/Exporte"), "Exporte");
const ExportAnsicht = bildschirm(() => import("./screens/exporte/ExportAnsicht"), "ExportAnsicht");
const BelegAnsicht = bildschirm(() => import("./screens/belege/BelegAnsicht"), "BelegAnsicht");
const DokumentAnsicht = bildschirm(() => import("./screens/belege/DokumentAnsicht"), "DokumentAnsicht");
const BogenAnsicht = bildschirm(() => import("./screens/belege/BogenAnsicht"), "BogenAnsicht");
const MengenAnsicht = bildschirm(() => import("./screens/belege/MengenAnsicht"), "MengenAnsicht");
const AbfrageAnsicht = bildschirm(() => import("./screens/belege/AbfrageAnsicht"), "AbfrageAnsicht");
const AntwortAnsicht = bildschirm(() => import("./screens/antwort/AntwortAnsicht"), "AntwortAnsicht");
const Admin = bildschirm(() => import("./screens/admin/Admin"), "Admin");

/** Ein leerer, ruhiger Rahmen (webgis `RuhigerRahmen`): eine Sitzungsprüfung liegt weit unter
 *  der 2-s-Schwelle für einen Lader. Dauert sie doch länger, erscheint der Halo. */
function RuhigerRahmen() {
  const lang = useVerzoegert(true, LOADER_MS);
  return (
    <div className="rahmen-leer lade-mitte" aria-busy="true">
      {lang && <Denkloader variante="halo" groesse={28} label="Lädt" />}
    </div>
  );
}

/* ------------------------------------------------------------------ Wurzel */

function Wurzel() {
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  const abgemeldet = useAuthStore((s) => s.abgemeldet);

  // DER EINE Aufruf von GET /api/me. Er läuft über dieselbe Abfrage, aus der jeder Bildschirm
  // liest — ein eigener fetch hier wäre eine zweite Anfrage, deren Antwort im Zwischenspeicher
  // nicht ankäme. Nach dem Abmelden wird nicht mehr gefragt.
  const { isSuccess, isError } = useIch(status !== "anonym");

  useEffect(() => {
    if (isSuccess) angemeldet();
    // Ein 401 hat den Status über den Riegel in lib/fetchJson schon gesetzt. Jeder ANDERE Fehler
    // (Netz, 503 während eines Deploys) heißt hier ebenfalls „nicht angemeldet“: es gibt nichts
    // zu zeigen, und die Anmeldung ist ein ehrlicheres Bild als ein hängender Rahmen.
    else if (isError) abgemeldet();
  }, [isSuccess, isError, angemeldet, abgemeldet]);

  return (
    <>
      <Outlet />
      {/* GENAU EINMAL in der ganzen Anwendung. */}
      <Toaster />
    </>
  );
}

/**
 * Auffangkorb der Wurzel (errorElement): fängt, was über dem Bildschirm-Korb abstürzt
 * (Wächter, Shell, Kopfleiste). Ohne ihn zeigte React Router seine englische Standardseite,
 * und nichts erreichte den Server. Meldet einmal, zeigt den deutschen Satz und „Neu laden“.
 */
function WurzelFehler() {
  const fehler = useRouteError();
  useEffect(() => {
    if (!(fehler instanceof ApiError)) fehlerMelden(fehler, null, "app");
  }, [fehler]);
  return (
    <div className="card hinweisseite" role="alert">
      <p style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{TX.fehler.umgefallen}</p>
      <div className="reihe wrap" style={{ justifyContent: "center", marginTop: 20 }}>
        <Btn
          type="button"
          icon="arrows-clockwise"
          onClick={() => {
            chunkRiegelLoesen();
            window.location.reload();
          }}
        >
          {TX.fehler.neuLaden}
        </Btn>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Wächter */

function Geschuetzt() {
  const status = useAuthStore((s) => s.status);
  const ort = useLocation();
  if (status === "unbekannt") return <RuhigerRahmen />;
  // Nach einem 401 aus irgendeinem Aufruf führt dieser Zweig zur Anmeldung — OHNE Toast: ein
  // abgelaufenes Cookie ist kein Fehler. `next` MIT der Suche (§7.3).
  if (status !== "angemeldet") return <Navigate to={"/anmelden?next=" + encodeURIComponent(ort.pathname + ort.search)} replace />;
  return <Outlet />;
}

/** `/admin/*` verlangt die Rolle admin (02 §1.7). */
function NurAdmin() {
  const { data: ich } = useIch();
  if (!ich) return <RuhigerRahmen />;
  if (!istAdmin(ich)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Auffangkorb + Nachladeanzeige um einen Bildschirm; `key` setzt beides je Weg zurück —
 *  in der Dokumentansicht je Dokument, nicht je Seite (Blättern ist kein neuer Bildschirm, E26).
 *  Einen Absturz vergisst der Korb aber bei JEDEM Wegwechsel (`zuruecksetzenBei`): sonst sperrte
 *  eine einzige kaputte Seite das ganze Dokument, auch nach „Zurück“ ins Raster (Review E26). */
function Bildschirm() {
  const ort = useLocation();
  return (
    <ScreenBoundary key={bildschirmSchluessel(ort.pathname)} zuruecksetzenBei={ort.pathname + ort.search}>
      <Suspense fallback={<RuhigerRahmen />}>
        <Outlet />
      </Suspense>
    </ScreenBoundary>
  );
}

/* ------------------------------------------------------------------ Verweise */

/** `/` — zum zuletzt benutzten Verfahren, sonst zum ersten; ohne Zugriff zur Übersicht. */
function Start() {
  const { data: ich } = useIch();
  const gemerkt = useStore((s) => s.verfahren);
  if (!ich) return <RuhigerRahmen />;
  const V = gemerkt && ich.verfahren.some((v) => v.code === gemerkt) ? gemerkt : ich.verfahren[0]?.code;
  return <Navigate to={V ? `/v/${encodeURIComponent(V)}/auswerten` : "/verfahren"} replace />;
}

function VNachAuswertung() {
  const { V } = useParams();
  return <Navigate to={`/v/${encodeURIComponent(V ?? "")}/auswerten`} replace />;
}

/** `/t/:slug?v=` → `/v/{V}/themen/:slug` (R03-16: Themen-Slugs gelten je Verfahren). */
function TVerweis() {
  const { slug } = useParams();
  const [such] = useSearchParams();
  const gemerkt = useStore((s) => s.verfahren);
  const V = such.get("v") ?? gemerkt;
  if (!V || !slug) return <Navigate to="/unbekannt" replace />;
  return <Navigate to={`/v/${encodeURIComponent(V)}/themen/${encodeURIComponent(slug)}`} replace />;
}

/**
 * `/r/:ref` innerhalb der Anwendung (R03-19): `GET /api/aufloesen/{ref}` → Ziel. Ganze
 * Seitenaufrufe von /r/… erreichen die SPA nie (nginx 302 → Backend). Parameter der Adresse
 * (`aus`, `rev`, `n`) wandern mit, sofern das Ziel sie nicht selbst setzt.
 */
function RVerweis() {
  const { ref } = useParams();
  const ort = useLocation();
  const navigate = useNavigate();
  const setzeVerfahren = useStore((s) => s.setzeVerfahren);
  useEffect(() => {
    if (!ref) return;
    let lebt = true;
    aufloesen(ref)
      .then((a) => {
        if (!lebt) return;
        if (a.typ === "unbekannt" || !a.ziel) {
          navigate(`/unbekannt?ref=${encodeURIComponent(ref)}`, { replace: true });
          return;
        }
        const V = a.verfahren_code ?? verfahrenAusCode(a.code);
        if (V) setzeVerfahren(V);
        const ziel = new URL(a.ziel, window.location.origin);
        new URLSearchParams(ort.search).forEach((w, k) => {
          if (!ziel.searchParams.has(k)) ziel.searchParams.set(k, w);
        });
        navigate(ziel.pathname + ziel.search + ziel.hash, { replace: true });
      })
      .catch((e: unknown) => {
        if (!lebt) return;
        if (e instanceof ApiError && e.status === 401) return; // der Riegel führt zur Anmeldung
        navigate(`/unbekannt?ref=${encodeURIComponent(ref)}`, { replace: true });
      });
    return () => {
      lebt = false;
    };
  }, [ref, ort.search, navigate, setzeVerfahren]);
  return <RuhigerRahmen />;
}

/** `/d/:dok/s/:n` — mit `?r=` die Beleg-Ansicht, ohne die Dokumentansicht auf Seite n (§7.1,
 *  E26). Die Dokumentansicht liest `dok`/`n` aus der Adresse; die Wegtabelle reicht keine Props durch. */
function DSeite() {
  const [such] = useSearchParams();
  return such.get("r") ? <BelegAnsicht /> : <DokumentAnsicht />;
}

/* ------------------------------------------------------------------ Wegtabelle */

const router = createBrowserRouter([
  {
    element: <Wurzel />,
    errorElement: <WurzelFehler />,
    children: [
      {
        path: "/anmelden",
        element: (
          <ScreenBoundary>
            <Anmelden />
          </ScreenBoundary>
        ),
      },
      {
        element: <Geschuetzt />,
        children: [
          {
            element: <Shell />,
            children: [
              {
                element: <Bildschirm />,
                children: [
                  { path: "/", element: <Start /> },
                  { path: "/verfahren", element: <VerfahrenBlatt /> },
                  { path: "/v/:V", element: <VNachAuswertung /> },
                  { path: "/v/:V/auswerten", element: <Auswertung /> },
                  {
                    // Prüfen mit seinen Reitern: Warteschlange, Bögen bilden, Fragenliste, Gold, Orte prüfen (E21), Eingang, Lauf.
                    element: <PruefenRahmen />,
                    children: [
                      { path: "/v/:V/pruefen", element: <Warteschlange /> },
                      { path: "/v/:V/pruefen/boegen", element: <Boegen /> },
                      { path: "/v/:V/pruefen/gold", element: <Gold /> },
                      { path: "/v/:V/pruefen/fragen", element: <Fragenliste /> },
                      // E35: Bogenvorlage aus dem leeren Bogen (03 §9.7.8)
                      { path: "/v/:V/pruefen/bogenvorlage", element: <Bogenvorlage /> },
                      { path: "/v/:V/pruefen/orte", element: <OrtePruefen /> },
                      { path: "/v/:V/pruefen/zeichnungen", element: <Zeichnungen /> },
                      { path: "/v/:V/eingang", element: <Eingang /> },
                      { path: "/v/:V/lauf", element: <Lauf /> },
                    ],
                  },
                  { path: "/v/:V/orte", element: <Orte /> },
                  { path: "/v/:V/karte", element: <Karte /> },
                  { path: "/v/:V/themen", element: <Themen /> },
                  { path: "/v/:V/themen/kodieren", element: <Kodieren /> },
                  { path: "/v/:V/themen/:slug", element: <ThemaSeite /> },
                  { path: "/t/:slug", element: <TVerweis /> },
                  { path: "/v/:V/exporte", element: <Exporte /> },
                  { path: "/x/:code", element: <ExportAnsicht /> },
                  { path: "/x/:code/k/:kennzahl", element: <ExportAnsicht /> },
                  { path: "/d/:dok", element: <DokumentAnsicht /> },
                  { path: "/d/:dok/s/:n", element: <DSeite /> },
                  { path: "/b/:bogen", element: <BogenAnsicht /> },
                  { path: "/m/:menge", element: <MengenAnsicht /> },
                  { path: "/q/:id", element: <AbfrageAnsicht /> },
                  { path: "/a/:id", element: <AntwortAnsicht /> },
                  { path: "/r/:ref", element: <RVerweis /> },
                  { path: "/unbekannt", element: <Unbekannt /> },
                  {
                    element: <NurAdmin />,
                    children: [
                      { path: "/admin", element: <Navigate to="/admin/nutzer" replace /> },
                      { path: "/admin/:reiter", element: <Admin /> },
                    ],
                  },
                  { path: "*", element: <NichtGefunden /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

// Absturzberichte nennen das Routenmuster (`/d/:dok/s/:n`), nie den Pfad mit Codes.
routenSetzen(router.routes);

export function App() {
  return <RouterProvider router={router} />;
}
